import { compareMeetingSessionRoles, isSpectator, MeetingContainerAPI, MeetingOrderedMedia, useMeetingSelector, useOrderedMeetingMedia } from "@kalyzee/kast-web-sdk";
import { MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { PageSlaveMode } from "../../components/navigation/PageContext";
import { ScreenConstants } from "../../constants/screens";
import { SessionMode } from "../../contexts/appStore/hooks";
import { ClientContainersIteration } from "../../helpers/bridge/common";
import { MessagingBridgeManagerMasterClient } from "../../helpers/bridge/master";
import { matchEngine } from "../../helpers/matchEngine";
import { SecondaryScreenLayout } from "../../interfaces/secondaryScreens";
import { useMeetingSources } from "../meeting";
import { useSettings } from "../settings";

export const DEFAULT_CAROUSEL_INTERVAL = 60000;
export const DEFAULT_CAROUSEL_DELAY = 5000;

const roundSplit = <T>(
  items: T[],
  screens: { capacity: number, rounds: { enabled: boolean, items: T[] }[] }[],
) => {
  let currRoundIndex = 0;
  let currScreenIndex = 0;

  const pushItem = (item: T): boolean => {
    const currScreen = screens[currScreenIndex];
    let currRound = currScreen.rounds.length > currRoundIndex ? currScreen.rounds[currRoundIndex] : undefined;
    if (currRound === undefined) {
      currRound = { enabled: true, items: [] };
      currScreen.rounds.push(currRound);
    }
    if (currRound.items.length >= currScreen.capacity) {
      if (currScreenIndex + 1 >= screens.length) {
        currScreenIndex = 0;
        currRoundIndex++;
        return pushItem(item);
      }
      currScreenIndex++;
      return pushItem(item);
    }
    currRound.items.push(item);
    return true;
  };

  items.forEach((i) => {
    pushItem(i);
  });
};

////////////////////////////////////////////////
//////////////////// MASTER ////////////////////
////////////////////////////////////////////////

interface Group {
  id: string,
  capacity: number,
  rounds: { enabled: boolean, items: MeetingOrderedMedia[] }[],
  roundIndex: number,
  nextRoundIndex?: number,
}

interface Carousel {
  id: string,
  fingerprint: string,
  media: MeetingOrderedMedia[],
  connectedMedia: MeetingOrderedMedia[],
  groups: Group[],
}

interface MainCarousel {
  media: MeetingOrderedMedia[],
  connectedMedia: MeetingOrderedMedia[],
  displayNextCarousel?: Date,
  displayNextCarouselTimeout?: NodeJS.Timer,
  /*
    1. The videos are split in groups
    2. Containers will define a video group
    3. For each video group, the videos are split among containers,
    taking their respective capacity into account. If there is not enough
    capacity, multiple rounds are created.

    In other words :
    2 containers with the same video group id will display different videos,
    in a temporal carousel fashion way.
  */
  carousels: Carousel[],
}

////////////////////////////////////////////////
//////////////////// CLIENT ////////////////////
////////////////////////////////////////////////

// Represents a column within a client,
// linked to a specific video carousel and video group
interface ClientContainer {
  id: string, // refers to the specific column id in the layout
  client: MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>,
  carouselId: string,
  groupId: string,
  capacity: number,
}

// ---- Helpers (pure, no hooks) ----

const getValidRoundIndex = (group: Group, startedAt: number): number => {
  if (!group.rounds.length) return 0;
  let currIndex = startedAt % group.rounds.length;
  const firstIndex = currIndex;
  let isValid = false;
  do {
    if (group.rounds[currIndex].enabled) {
      isValid = true;
      break;
    }
    currIndex = (currIndex + 1) % group.rounds.length;
  } while (currIndex !== firstIndex);
  return isValid ? currIndex : 0;
};

const convertToPageInfo = (idx: number, rounds: Group['rounds']): {
  page: number,
  pageCount: number,
  round?: Group['rounds'][number],
} => {
  let page = -1;
  let pageCount = 0;
  let selectedRound: Group['rounds'][number] | undefined = undefined;

  // We try to get back on the index we were before.
  // If not possible, we go to the next available page after this index.
  // If there is none, we loop back to the first available page.
  rounds.forEach((round, roundIdx) => {
    if (round.enabled) {
      if (roundIdx < idx) {
        page++;
      } else {
        if (!selectedRound) {
          selectedRound = round;
          page++;
        }
      }
      pageCount++;
    }
  });

  return { page: selectedRound ? page : 0, pageCount, round: selectedRound };
};

/**
 * Builds a fingerprint for a single carousel that captures:
 * - which media are present and their connected state (composition)
 * - which groups exist and their capacities (container structure)
 * This ensures changes in either media or client/container layout trigger a re-dispatch.
 */
const buildCarouselFingerprint = (
  filteredMedia: MeetingOrderedMedia[],
  groups: { id: string, capacity: number }[],
): string => {
  const mediaPart = filteredMedia.map(m => `${m.media.id}:${m.connected ? 1 : 0}`).join(",");
  const groupsPart = groups.map(g => `${g.id}:${g.capacity}`).join(",");
  return `${mediaPart}|${groupsPart}`;
};

// ---- Hook ----

export const useVideoCarousel = ({
  meetingAPI,
  getClients,
  onCarousel,
}: {
  meetingAPI?: MeetingContainerAPI,
  getClients: () => MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[],
  onCarousel: (data: ClientContainersIteration, delay: number, id: string) => void,
}) => {
  const [settings] = useSettings();

  const [meeting] = useMeetingSelector(meetingAPI, "meeting");
  const [session] = useMeetingSelector(meetingAPI, "session");
  const [sessions] = useMeetingSelector(meetingAPI, "sessions");

  const orderedMedia = useOrderedMeetingMedia(meeting, sessions, useCallback((_, s) => {
    if (!session) return false;
    return true;
  }, [session]));

  const carouselIntervalRef = useRef<NodeJS.Timer>();
  const carouselInfoRef = useRef<MainCarousel>();

  const sessionsMappedById = useMemo(() => {
    const map: { [key: string]: MeetingSession } = {};
    sessions?.forEach((s) => { map[s.id] = s; });
    return map;
  }, [sessions]);

  const layoutsMap: { [key: number]: SecondaryScreenLayout | undefined } = useMemo(
    () => Object.fromEntries(
      (settings.carousel?.layouts ?? [])?.map(layout => ([layout.id, layout])),
    ),
    [settings],
  );

  const getLayout = useCallback((layoutId: number | undefined) => {
    let layout = layoutsMap[layoutId as any];
    if (layout) return layout;
    layout = layoutsMap[undefined as any];
    if (layout) return layout;
    return ScreenConstants.DefaultLayout;
  }, [layoutsMap]);

  const getClientContainers = useCallback((
    client: MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>
  ): ClientContainer[] => {
    const layoutId = client.data?.data?.layoutId;
    const layout = getLayout(layoutId);
    if (!layout.videoContainers) return [];
    return layout.videoContainers.map((container, idx) => ({
      client,
      id: container.id,
      carouselId: container.carouselId ?? "default",
      groupId: container.groupId ?? `${client.id}-${idx}`,
      capacity: container.capacity,
    }));
  }, [getLayout]);

  /**
   *  Carousel :
   *    Group :
   *      ClientContainer[]
   * There can be multiple ClientContainer per client.
   * A ClientContainer represents a column in a client layout, bound to a carousel and a group.
   */
  const getClientContainersMappedByCarouselAndGroup = useCallback((): Map<string, Map<string, ClientContainer[]>> => {
    const clients = getClients();
    const map = new Map<string, Map<string, ClientContainer[]>>();

    clients.forEach((client) => {
      const containers = getClientContainers(client);
      containers.forEach((clientContainer) => {
        const { carouselId, groupId } = clientContainer;

        let carousel = map.get(carouselId);
        if (!carousel) {
          carousel = new Map<string, ClientContainer[]>();
          map.set(carouselId, carousel);
        }

        let group = carousel.get(groupId);
        if (!group) {
          group = [];
          carousel.set(groupId, group);
        }

        group.push(clientContainer);
      });
    });

    return map;
  }, [getClients, getClientContainers]);

  // --- Refs for latest values (avoids stale closures in interval/timeout callbacks) ---

  const orderedMediaRef = useRef(orderedMedia);
  orderedMediaRef.current = orderedMedia;

  const sessionsMappedByIdRef = useRef(sessionsMappedById);
  sessionsMappedByIdRef.current = sessionsMappedById;

  const settingsCarouselFiltersRef = useRef(settings.carousel?.filters);
  settingsCarouselFiltersRef.current = settings.carousel?.filters;

  const getClientContainersMappedByCarouselAndGroupRef = useRef(getClientContainersMappedByCarouselAndGroup);
  getClientContainersMappedByCarouselAndGroupRef.current = getClientContainersMappedByCarouselAndGroup;

  const onCarouselRef = useRef(onCarousel);
  onCarouselRef.current = onCarousel;

  const sources = useMeetingSources();
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;

  const sessionRef = useRef(session);
  sessionRef.current = session;

  // --- Core carousel logic ---

  /**
   * Rebuilds the carousel info structure from current state.
   * Returns the set of carousel ids whose fingerprint changed (i.e. whose
   * composition actually differs from the previous state).
   * Returns undefined if there was no previous state (first run).
   */
  const generateCarouselInfo = useCallback((): Set<string> | undefined => {
    const media = orderedMediaRef.current;
    const previousCarouselInfo = carouselInfoRef.current;
    const newCarouselData: MainCarousel = {
      media,
      connectedMedia: media.filter(m => m.connected),
      displayNextCarousel: previousCarouselInfo?.displayNextCarousel,
      displayNextCarouselTimeout: previousCarouselInfo?.displayNextCarouselTimeout,
      carousels: [],
    };
    carouselInfoRef.current = newCarouselData;

    const containersByCarouselAndGroup = getClientContainersMappedByCarouselAndGroupRef.current();
    const filters = settingsCarouselFiltersRef.current;
    const sessionsById = sessionsMappedByIdRef.current;

    const changedCarouselIds = previousCarouselInfo ? new Set<string>() : undefined;

    containersByCarouselAndGroup.forEach((containersByGroup, carouselId) => {
      const customFilter = filters?.find(f => f.carouselId === carouselId)?.value;

      let filteredMedia = media;
      if (customFilter) {
        filteredMedia = media.filter((m) => {
          const freshSession = sessionsById[m.session.id];
          if (!freshSession) return false;
          const freshMedia = freshSession.medias.find(med => med.id === m.media.id);
          if (!freshMedia) return false;
          return matchEngine(
            { session: freshSession, media: freshMedia, currentSession: sessionRef.current },
            customFilter,
            { "session.role": (a, b) => -compareMeetingSessionRoles(a, b) },
          );
        });
      }

      const previousCarousel = previousCarouselInfo?.carousels.find(c => c.id === carouselId);

      const groups: Group[] = [];

      containersByGroup.forEach((containers, groupId) => {
        if (!containers.length) return;

        const previousGroup = previousCarousel?.groups.find(c => c.id === groupId);

        const group: Group = {
          id: groupId,
          capacity: 0,
          rounds: [],
          roundIndex: previousGroup?.roundIndex ?? 0,
          nextRoundIndex: previousGroup?.nextRoundIndex,
        };

        let capacity = Infinity;
        containers.forEach(c => { capacity = Math.min(capacity, c.capacity); });
        group.capacity = capacity;

        groups.push(group);
      });

      roundSplit<MeetingOrderedMedia>(filteredMedia, groups);

      // Disable rounds containing only disconnected users
      groups.forEach((group) => {
        group.rounds.forEach((round) => {
          if (!round.items.some(s => s.connected)) {
            round.enabled = false;
          }
        });
        group.roundIndex = getValidRoundIndex(group, group.roundIndex);
        if (group.nextRoundIndex !== undefined) {
          group.nextRoundIndex = getValidRoundIndex(group, group.nextRoundIndex);
        }
      });

      // Build fingerprint from filtered media + group structure
      const fingerprint = buildCarouselFingerprint(filteredMedia, groups);
      if (changedCarouselIds && fingerprint !== previousCarousel?.fingerprint) {
        changedCarouselIds.add(carouselId);
      }

      newCarouselData.carousels.push({
        id: carouselId,
        fingerprint,
        media: filteredMedia,
        connectedMedia: filteredMedia.filter(m => m.connected),
        groups,
      });
    });

    return changedCarouselIds;
  }, []);

  /**
   * Builds the ClientContainersIteration map for all clients based on current carousel state.
   * If changedCarouselIds is provided, only clients that have at least one changed carousel
   * will be included (but with their full state, since the slave replaces everything).
   */
  const buildClientIterations = useCallback((
    carouselInfo: MainCarousel,
    getRoundIndex: (group: Group) => number,
    changedCarouselIds?: Set<string>,
  ): Map<string, ClientContainersIteration> => {
    const containersByCarouselAndGroup = getClientContainersMappedByCarouselAndGroupRef.current();
    const clientContainersIteration = new Map<string, ClientContainersIteration>();
    // Track which clients are affected by a changed carousel
    const affectedClientIds = new Set<string>();

    const getOrCreate = (clientId: string): ClientContainersIteration => {
      let entry = clientContainersIteration.get(clientId);
      if (!entry) {
        entry = { containers: [] };
        clientContainersIteration.set(clientId, entry);
      }
      return entry;
    };

    carouselInfo.carousels.forEach((carousel) => {
      const containersByGroup = containersByCarouselAndGroup.get(carousel.id);
      const isChanged = !changedCarouselIds || changedCarouselIds.has(carousel.id);

      carousel.groups.forEach((group) => {
        const containers = containersByGroup?.get(group.id);

        if (!group.rounds.length) {
          containers?.forEach((container) => {
            if (isChanged) affectedClientIds.add(container.client.id);

            // We build this container, even tho this carousel might be inchanged
            // Maybe the client will have atleast 1 changed carousel,
            // in this case we want to send the full state of all carousels, including the unchanged ones

            getOrCreate(container.client.id).containers.push({
              id: container.id,
              sessions: [],
              totalSessions: 0,
              page: 0,
              pageCount: 0,
              carouselId: container.carouselId,
              groupId: container.groupId,
            });
          });
          return;
        }

        const roundIdx = getRoundIndex(group);

        containers?.forEach((container) => {
          if (isChanged) affectedClientIds.add(container.client.id);

          // Same logic than above, building container anyway ^

          const currCapacity = Math.min(group.capacity, container.capacity);
          const pageInfo = convertToPageInfo(roundIdx, group.rounds);

          const items = pageInfo.round ? pageInfo.round.items.slice(0, currCapacity) : [];

          // Annotate master's own medias with their source index in settings.sources
          const masterSessionId = sessionRef.current?.id;
          const sourceIndexes: Record<string, number> = {};
          items.forEach((s) => {
            if (masterSessionId && s.session.id === masterSessionId) {
              const idx = sourcesRef.current.findIndex(src => src.name === s.media.name);
              if (idx >= 0) sourceIndexes[s.media.id] = idx;
            }
          });

          getOrCreate(container.client.id).containers.push({
            id: container.id,
            sessions: items.map((s) => s.media),
            sourceIndexes: Object.keys(sourceIndexes).length ? sourceIndexes : undefined,
            totalSessions: carousel.connectedMedia.length,
            page: pageInfo.page,
            pageCount: pageInfo.pageCount,
            carouselId: container.carouselId,
            groupId: container.groupId,
          });
        });
      });
    });

    // If filtering by changed carousels, remove clients that aren't affected by any changed carousel
    if (changedCarouselIds) {
      clientContainersIteration.forEach((_, clientId) => {
        if (!affectedClientIds.has(clientId)) {
          clientContainersIteration.delete(clientId);
        }
      });
    }

    return clientContainersIteration;
  }, []);

  const sendIterations = useCallback((iterations: Map<string, ClientContainersIteration>, delay: number) => {
    iterations.forEach((iteration, clientId) => {
      onCarouselRef.current(iteration, delay, clientId);
    });
  }, []);

  const setNext = useCallback((
    mode: 'increment' | 'restart',
    delay: number = 0,
  ) => {
    const carouselInfo = carouselInfoRef.current;
    if (!carouselInfo) return;

    // Compute next round indexes
    carouselInfo.carousels.forEach((carousel) => {
      carousel.groups.forEach((group) => {
        if (!group.rounds.length) return;
        let nextRoundIndex = getValidRoundIndex(group, group.roundIndex);
        if (mode === "restart") nextRoundIndex = getValidRoundIndex(group, 0);
        else if (mode === 'increment') nextRoundIndex = getValidRoundIndex(group, group.roundIndex + 1);
        group.nextRoundIndex = nextRoundIndex;
      });
    });

    const iterations = buildClientIterations(
      carouselInfo,
      (group) => group.nextRoundIndex ?? 0,
    );
    sendIterations(iterations, delay);

    if (carouselInfo.displayNextCarouselTimeout) clearTimeout(carouselInfo.displayNextCarouselTimeout);

    const updateIndexes = () => {
      carouselInfo.displayNextCarousel = undefined;
      carouselInfo.carousels.forEach((carousel) => {
        carousel.groups.forEach((group) => {
          if (group.nextRoundIndex !== undefined) group.roundIndex = group.nextRoundIndex;
          group.nextRoundIndex = undefined;
        });
      });
    };

    if (delay > 0) {
      carouselInfo.displayNextCarousel = new Date(Date.now() + delay);
      carouselInfo.displayNextCarouselTimeout = setTimeout(updateIndexes, delay);
    } else {
      updateIndexes();
      carouselInfo.displayNextCarousel = undefined;
      carouselInfo.displayNextCarouselTimeout = undefined;
    }
  }, [buildClientIterations, sendIterations]);

  /**
   * Rebuilds carousel info and sends updates only for carousels whose
   * composition actually changed (different filtered media).
   */
  const sendCurrentState = useCallback(() => {
    if (!carouselIntervalRef.current) return;

    const changedCarouselIds = generateCarouselInfo();
    const carouselInfo = carouselInfoRef.current;
    if (!carouselInfo) return;

    // Nothing changed — skip entirely
    if (changedCarouselIds && changedCarouselIds.size === 0) return;

    const iterations = buildClientIterations(
      carouselInfo,
      (group) => {
        group.roundIndex = getValidRoundIndex(group, group.roundIndex);
        return group.roundIndex;
      },
      changedCarouselIds,
    );
    sendIterations(iterations, 0);

    /**
     * Bug fix for fast updates colliding with a pending carousel transition:
     * If setNext was called with a delay (e.g. from the interval), and then
     * sendCurrentState fires before that delay expires, it would reset slaves
     * back to the current roundIndex (N), while the pending timeout would later
     * set roundIndex to N+1.
     *
     * Fix: re-issue setNext with the remaining delay so the pending transition
     * is re-sent to slaves.
     */
    if (carouselInfo.displayNextCarousel) {
      const remaining = carouselInfo.displayNextCarousel.getTime() - Date.now();
      setNext("increment", Math.max(0, remaining));
    }
  }, [generateCarouselInfo, buildClientIterations, sendIterations, setNext]);

  const setNextRef = useRef(setNext);
  setNextRef.current = setNext;

  const sendCurrentStateRef = useRef(sendCurrentState);
  sendCurrentStateRef.current = sendCurrentState;

  const stop = useCallback(() => {
    if (carouselIntervalRef.current) clearInterval(carouselIntervalRef.current);
    if (carouselInfoRef.current?.displayNextCarouselTimeout) clearTimeout(carouselInfoRef.current.displayNextCarouselTimeout);
    carouselIntervalRef.current = undefined;
    carouselInfoRef.current = undefined;
  }, []);

  const start = useCallback(() => {
    stop();
    generateCarouselInfo();
    setNext('restart', 0);

    const interval = settings?.carousel?.interval ?? DEFAULT_CAROUSEL_INTERVAL;
    const preloadDelay = settings?.carousel?.prelaodDelay ?? DEFAULT_CAROUSEL_DELAY;

    carouselIntervalRef.current = setInterval(() => {
      setNextRef.current('increment', preloadDelay);
    }, interval);
  }, [stop, generateCarouselInfo, setNext, settings?.carousel?.prelaodDelay, settings?.carousel?.interval]);

  const isRunning = useCallback(() => {
    return carouselIntervalRef.current !== undefined;
  }, []);

  // Rebuild carousel when meeting, sessions, or current session change:
  // - meeting: meeting state/settings update
  // - sessions: participant join/leave, media added/removed
  // - session: current session update (e.g. role change)
  // matchEngine filters depend on all three
  useEffect(() => {
    if (!meeting) {
      stop();
      return;
    }

    const clients = getClients();
    if (!isRunning() && clients.length) {
      start();
      return;
    }

    if (isRunning()) {
      sendCurrentStateRef.current();
    }
  }, [meeting, session, sessions, getClients, start, stop, isRunning]);

  return {
    update: sendCurrentState,
    start,
    stop,
    isRunning,
  };
};
